import React, { useState } from "react";
import { Container, Grid, GridColumn, Header, List, ListItem, Segment } from "@/components/bp/bp-ui";
import { PageHeader } from "@common/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { roleClient } from "@app/api2/Clients";
import SemanticFormikCheckbox from "@common/Forms/SemanticFormikCheckbox";
import { useUser } from "../Contexts/UserContext";
import { CreateRoleDialog } from "./CreateRoleDialog";
import { groupBy } from "lodash";
import { groupByMap, keyOfCheckerNested } from "@common/Utils";
import { BPContainer } from "@/components/bp/bpcontainer";

type RolesFormValues = {
    permissions: string[];
}
const fn = keyOfCheckerNested<RolesFormValues>();

export const Roles: React.FC = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    const query = useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            const allRoles = await roleClient.getAll()
            const allPerms = await roleClient.getAllPermissions();
            return { allRoles, allPerms }
        }
    });

    const handleCreateRole = async (roleName: string) => {
        await roleClient.create({ name: roleName });
        await query.refetch();
    };

    const currentUser = useUser();


    return (
        <BPContainer>
            <PageHeader
                header={window.translation.common.roles}
                buttons={currentUser.user.isSiteAdmin ? [
                    {
                        label: window.translation.common.createNew,
                        action: async () => setCreateDialogOpen(true)
                    }
                ] : []}
            />
            <CreateRoleDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCreate={handleCreateRole}
            />

            {query.data?.allRoles?.map(role => (
                <Segment key={role.id}>
                    <h3 style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>{role.name}</span>
                    </h3>
                    <Segment secondary>
                        <Formik
                            initialValues={{
                                permissions: (role.permissions || [])
                            }}
                            initialTouched={{}}
                            enableReinitialize
                            validationSchema={Yup.object({
                                permissions: Yup.array().of(Yup.string())
                            })}
                            onSubmit={async (values, actions) => {
                                await roleClient.updatePermissions(role.id, values.permissions)
                                await query.refetch();

                                actions.setSubmitting(false);
                            }}
                        >
                            {({ values, isSubmitting, handleSubmit, touched }) => (
                                <Form onSubmit={handleSubmit}>
                                    <FieldArray name={fn("permissions")}>
                                        {() => (<>
                                            <Grid columns={4} >
                                                {Array.from(groupByMap(query.data.allPerms, perm => perm.code.split('.')[0]))?.map((grp, grpidx) => {
                                                    return <GridColumn key={grp[0]}>
                                                        <Header as="h4">{grp[0]}</Header>
                                                        <List>
                                                            {grp[1].map((perm, idx) => {
                                                                return <ListItem key={perm.code}>
                                                                    <SemanticFormikCheckbox
                                                                        id={`permission_${role.id}_${grpidx}_${idx}`}
                                                                        key={perm.code}
                                                                        type="checkbox"
                                                                        name={fn("permissions")}
                                                                        label={perm.code.split('.').slice(1)}
                                                                        value={perm.code}
                                                                        checked={values.permissions.includes(perm.code)}
                                                                        style={{ minWidth: 200 }} />
                                                                </ListItem>
                                                            })}
                                                        </List>
                                                    </GridColumn>
                                                })}
                                            </Grid>

                                            <Segment basic textAlign="right">
                                                <button type="submit" className="ui primary button" disabled={!touched.permissions || isSubmitting}>
                                                    Update Permissions
                                                </button>

                                                {currentUser.user.isSiteAdmin && (
                                                    <button
                                                        className="ui red button"
                                                        style={{ marginLeft: 16 }}
                                                        onClick={async () => {
                                                            if (window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) {
                                                                await roleClient.delete(role.id);
                                                                await query.refetch();
                                                            }
                                                        }}>
                                                        {window.translation.common.delete}
                                                    </button>
                                                )}

                                            </Segment>
                                        </>
                                        )}
                                    </FieldArray>
                                </Form>
                            )}
                        </Formik>
                    </Segment>
                </Segment>
            ))}
        </BPContainer>
    );
};
